import DataRowActions from "./DataRowActions";
import Pager from "@/components/Pager";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/i18n/LanguageContext";

const renderValue = (field, item) => {
  if (typeof field.value === "function") return field.value(item);
  return item[field.value];
};

/**
 * Config-driven protected-list renderer.
 *
 * columns: [{ key, header, cell, className }]
 * mobileFields: [{ label, value, priority }]
 * rowActions: [{ key, label, icon, onClick, hidden, destructive, testId }]
 */
export default function ResponsiveDataView({
  items = [],
  loading = false,
  emptyState,
  loadingState,
  getRowKey,
  getRowTestId,
  columns = [],
  mobileFields = [],
  mobileTitle,
  mobileSubtitle,
  mobileBadge,
  rowActions = [],
  pagination,
  tableTestId,
  loadingTestId,
  emptyTestId,
}) {
  const t = useTranslation();
  const hasItems = items.length > 0;

  const stateContent = loading
    ? (loadingState || t("common.table.loading"))
    : (emptyState || t("common.table.noRecords"));
  const stateTestId = loading ? loadingTestId : emptyTestId;

  return (
    <section className="space-y-3">
      <div className="hidden overflow-hidden rounded-xl border border-border bg-card md:block">
        <table className="w-full border-collapse" data-testid={tableTestId}>
          <thead>
            <tr className="bg-muted/50 text-left">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn(
                    "border-b border-border px-3 py-2.5 font-display text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground",
                    column.className
                  )}
                >
                  {column.header}
                </th>
              ))}
              {rowActions.length > 0 && (
                <th className="border-b border-border px-3 py-2.5 text-right font-display text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  {t("common.table.actions")}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading || !hasItems ? (
              <tr>
                <td
                  colSpan={columns.length + (rowActions.length > 0 ? 1 : 0)}
                  className="px-3 py-10 text-center text-xs text-muted-foreground"
                  data-testid={stateTestId}
                >
                  {stateContent}
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={getRowKey(item)}
                  className="border-b border-border transition-colors last:border-b-0 hover:bg-muted"
                  data-testid={getRowTestId?.(item)}
                >
                  {columns.map((column) => (
                    <td key={column.key} className={cn("px-3 py-2 text-[13px]", column.cellClassName)}>
                      {column.cell(item)}
                    </td>
                  ))}
                  {rowActions.length > 0 && (
                    <td className="px-3 py-2 text-right">
                      <DataRowActions actions={rowActions} item={item} compact className="justify-end" />
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="space-y-3 md:hidden">
        {loading || !hasItems ? (
          <div
            className="rounded-xl border border-border px-4 py-10 text-center text-xs text-muted-foreground"
            data-testid={stateTestId}
          >
            {stateContent}
          </div>
        ) : (
          items.map((item) => (
            <article
              key={getRowKey(item)}
              className="rounded-xl border border-border bg-card p-4"
              data-testid={getRowTestId?.(item)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="break-words text-sm font-medium">
                    {mobileTitle ? mobileTitle(item) : columns[0]?.cell(item)}
                  </div>
                  {mobileSubtitle && (
                    <div className="mt-1 break-words font-mono text-[11px] text-muted-foreground">
                      {mobileSubtitle(item)}
                    </div>
                  )}
                </div>
                {mobileBadge && <div className="shrink-0">{mobileBadge(item)}</div>}
              </div>

              <dl className="mt-4 grid gap-3 text-xs">
                {mobileFields.map((field) => (
                  <div
                    key={field.key || field.label}
                    className={cn(
                      "grid grid-cols-[7.5rem_minmax(0,1fr)] gap-3",
                      field.priority === "secondary" && "text-muted-foreground"
                    )}
                  >
                    <dt className="mono-eyebrow !text-[9px]">{field.label}</dt>
                    <dd className="min-w-0 break-words text-right font-mono">{renderValue(field, item)}</dd>
                  </div>
                ))}
              </dl>

              {rowActions.length > 0 && (
                <DataRowActions actions={rowActions} item={item} className="mt-4 border-t border-border pt-3" />
              )}
            </article>
          ))
        )}
      </div>

      {pagination && <Pager {...pagination} />}
    </section>
  );
}
